import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Hotel as HostelIcon,
  Bed as BedIcon,
  Person as PersonIcon,
  Home as RoomIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { motion } from 'framer-motion';

const HostelManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  const [hostels, setHostels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('hostel');
  const [editingItem, setEditingItem] = useState(null);
  const [stats, setStats] = useState({
    totalHostels: 0,
    totalRooms: 0,
    occupiedRooms: 0,
    totalStudents: 0
  });

  const [formData, setFormData] = useState({
    hostel_name: '',
    hostel_type: 'boys',
    capacity: '',
    warden_name: '',
    warden_phone: '',
    address: '',
    facilities: '',
    room_number: '',
    hostel_id: '',
    room_type: 'single',
    room_capacity: '',
    student_id: '',
    room_id: '',
    allocation_date: new Date().toISOString().split('T')[0],
    fee_amount: '',
    is_active: true
  });

  const hostelTypes = [
    { value: 'boys', label: 'Boys Hostel' },
    { value: 'girls', label: 'Girls Hostel' },
    { value: 'mixed', label: 'Mixed Hostel' }
  ];

  const roomTypes = [
    { value: 'single', label: 'Single Room' },
    { value: 'double', label: 'Double Room' },
    { value: 'triple', label: 'Triple Room' },
    { value: 'dormitory', label: 'Dormitory' }
  ];

  useEffect(() => {
    fetchHostels();
    fetchRooms();
    fetchAllocations();
    fetchStudents();
    fetchStats();
  }, []);

  const fetchHostels = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/hostels');
      if (response.data.success) {
        setHostels(response.data.data || []);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch hostels', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await axios.get('/api/hostels/rooms');
      if (response.data.success) {
        setRooms(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
    }
  };

  const fetchAllocations = async () => {
    try {
      const response = await axios.get('/api/hostels/allocations');
      if (response.data.success) {
        setAllocations(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch allocations:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get('/api/students');
      if (response.data.success) {
        setStudents(response.data.data.students || []);
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/hostels/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      let url, method;
      let data = { ...formData };
      
      if (dialogType === 'hostel') {
        url = editingItem ? `/api/hostels/${editingItem.hostel_id}` : '/api/hostels';
        method = editingItem ? 'put' : 'post';
      } else if (dialogType === 'room') {
        url = editingItem ? `/api/hostels/rooms/${editingItem.room_id}` : '/api/hostels/rooms';
        method = editingItem ? 'put' : 'post';
      } else if (dialogType === 'allocation') {
        url = editingItem ? `/api/hostels/allocations/${editingItem.allocation_id}` : '/api/hostels/allocations';
        method = editingItem ? 'put' : 'post';
      }
      
      const response = await axios[method](url, data);
      
      if (response.data.success) {
        enqueueSnackbar(
          `${dialogType.charAt(0).toUpperCase() + dialogType.slice(1)} ${editingItem ? 'updated' : 'created'} successfully`,
          { variant: 'success' }
        );
        setOpenDialog(false);
        resetForm();
        fetchHostels();
        fetchRooms();
        fetchAllocations();
        fetchStats();
      }
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || 'Operation failed',
        { variant: 'error' }
      );
    }
  };

  const handleEdit = (item, type) => {
    setEditingItem(item);
    setDialogType(type);
    
    if (type === 'hostel') {
      setFormData({
        ...formData,
        hostel_name: item.hostel_name || '',
        hostel_type: item.hostel_type || 'boys',
        capacity: item.capacity || '',
        warden_name: item.warden_name || '',
        warden_phone: item.warden_phone || '',
        address: item.address || '',
        facilities: item.facilities || '',
        is_active: item.is_active !== undefined ? item.is_active : true
      });
    } else if (type === 'room') {
      setFormData({
        ...formData,
        room_number: item.room_number || '',
        hostel_id: item.hostel_id || '',
        room_type: item.room_type || 'single',
        room_capacity: item.room_capacity || '',
        is_active: item.is_active !== undefined ? item.is_active : true
      });
    } else if (type === 'allocation') {
      setFormData({
        ...formData,
        student_id: item.student_id || '',
        room_id: item.room_id || '',
        allocation_date: item.allocation_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        fee_amount: item.fee_amount || '',
        is_active: item.is_active !== undefined ? item.is_active : true
      });
    }
    
    setOpenDialog(true);
  };

  const handleDelete = async (itemId, type) => {
    if (window.confirm(`Are you sure you want to delete this ${type}?`)) {
      try {
        let url;
        if (type === 'hostel') url = `/hostels/${itemId}`;
        else if (type === 'room') url = `/hostels/rooms/${itemId}`;
        else if (type === 'allocation') url = `/hostels/allocations/${itemId}`;
        
        const response = await axios.delete(`/api${url}`);
        if (response.data.success) {
          enqueueSnackbar(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`, { variant: 'success' });
          fetchHostels();
          fetchRooms();
          fetchAllocations();
          fetchStats();
        }
      } catch (error) {
        enqueueSnackbar(`Failed to delete ${type}`, { variant: 'error' });
      }
    }
  };

  const resetForm = () => {
    setFormData({
      hostel_name: '',
      hostel_type: 'boys',
      capacity: '',
      warden_name: '',
      warden_phone: '',
      address: '',
      facilities: '',
      room_number: '',
      hostel_id: '',
      room_type: 'single',
      room_capacity: '',
      student_id: '',
      room_id: '',
      allocation_date: new Date().toISOString().split('T')[0],
      fee_amount: '',
      is_active: true
    });
    setEditingItem(null);
  };

  const openAddDialog = (type) => {
    resetForm();
    setDialogType(type);
    setOpenDialog(true);
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Hostel Management
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.totalHostels}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Total Hostels
                    </Typography>
                  </Box>
                  <HostelIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.totalRooms}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Total Rooms
                    </Typography>
                  </Box>
                  <RoomIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.occupiedRooms}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Occupied Rooms
                    </Typography>
                  </Box>
                  <BedIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {stats.totalStudents}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Hostel Students
                    </Typography>
                  </Box>
                  <PersonIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Hostels" />
          <Tab label="Rooms" />
          <Tab label="Allocations" />
        </Tabs>
      </Paper>

      {/* Hostels Tab */}
      <TabPanel value={tabValue} index={0}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchHostels}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openAddDialog('hostel')}
            >
              Add Hostel
            </Button>
          </Box>
        </Paper>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Hostel Details</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Capacity</TableCell>
                  <TableCell>Warden</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">Loading...</TableCell>
                  </TableRow>
                ) : hostels.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No hostels found</TableCell>
                  </TableRow>
                ) : (
                  hostels.map((hostel) => (
                    <TableRow key={hostel.hostel_id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                            {hostel.hostel_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {hostel.address}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={hostel.hostel_type?.charAt(0).toUpperCase() + hostel.hostel_type?.slice(1)}
                          color={hostel.hostel_type === 'boys' ? 'primary' : hostel.hostel_type === 'girls' ? 'secondary' : 'info'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{hostel.capacity}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {hostel.warden_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {hostel.warden_phone}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={hostel.is_active ? 'Active' : 'Inactive'}
                          color={hostel.is_active ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="Edit Hostel">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(hostel, 'hostel')}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Hostel">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(hostel.hostel_id, 'hostel')}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* Rooms Tab */}
      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchRooms}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openAddDialog('room')}
            >
              Add Room
            </Button>
          </Box>
        </Paper>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Room Number</TableCell>
                  <TableCell>Hostel</TableCell>
                  <TableCell>Room Type</TableCell>
                  <TableCell>Capacity</TableCell>
                  <TableCell>Occupancy</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rooms.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No rooms found</TableCell>
                  </TableRow>
                ) : (
                  rooms.map((room) => (
                    <TableRow key={room.room_id} hover>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                          {room.room_number}
                        </Typography>
                      </TableCell>
                      <TableCell>{room.hostel_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={room.room_type?.charAt(0).toUpperCase() + room.room_type?.slice(1)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{room.capacity}</TableCell>
                      <TableCell>{room.current_occupancy || 0}</TableCell>
                      <TableCell>
                        <Chip
                          label={room.is_active ? 'Active' : 'Inactive'}
                          color={room.is_active ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="Edit Room">
                            <IconButton size="small" onClick={() => handleEdit(room, 'room')} color="primary">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Room">
                            <IconButton size="small" onClick={() => handleDelete(room.room_id, 'room')} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* Allocations Tab */}
      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchAllocations}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openAddDialog('allocation')}
            >
              Allocate Room
            </Button>
          </Box>
        </Paper>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Hostel</TableCell>
                  <TableCell>Room</TableCell>
                  <TableCell>Allocation Date</TableCell>
                  <TableCell>Fee Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allocations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No allocations found</TableCell>
                  </TableRow>
                ) : (
                  allocations.map((alloc) => (
                    <TableRow key={alloc.allocation_id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: 14 }}>
                            {alloc.student_name?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                              {alloc.student_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {alloc.student_code}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{alloc.hostel_name}</TableCell>
                      <TableCell>{alloc.room_number}</TableCell>
                      <TableCell>{alloc.allocation_date ? new Date(alloc.allocation_date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>Rs. {alloc.fee_amount || 0}</TableCell>
                      <TableCell>
                        <Chip
                          label={alloc.status?.charAt(0).toUpperCase() + alloc.status?.slice(1)}
                          color={alloc.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="Edit Allocation">
                            <IconButton size="small" onClick={() => handleEdit(alloc, 'allocation')} color="primary">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove Allocation">
                            <IconButton size="small" onClick={() => handleDelete(alloc.allocation_id, 'allocation')} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* Dialog for Add/Edit */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingItem ? 'Edit' : 'Add'} {dialogType.charAt(0).toUpperCase() + dialogType.slice(1)}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {dialogType === 'hostel' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Hostel Name"
                    value={formData.hostel_name}
                    onChange={(e) => setFormData({ ...formData, hostel_name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Hostel Type</InputLabel>
                    <Select
                      value={formData.hostel_type}
                      label="Hostel Type"
                      onChange={(e) => setFormData({ ...formData, hostel_type: e.target.value })}
                    >
                      {hostelTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Capacity"
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Warden Name"
                    value={formData.warden_name}
                    onChange={(e) => setFormData({ ...formData, warden_name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Warden Phone"
                    value={formData.warden_phone}
                    onChange={(e) => setFormData({ ...formData, warden_phone: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    multiline
                    rows={2}
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Facilities"
                    multiline
                    rows={3}
                    value={formData.facilities}
                    onChange={(e) => setFormData({ ...formData, facilities: e.target.value })}
                    placeholder="Wi-Fi, Laundry, Mess, Library, etc."
                  />
                </Grid>
              </>
            )}

            {dialogType === 'room' && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Hostel</InputLabel>
                    <Select
                      value={formData.hostel_id}
                      label="Hostel"
                      onChange={(e) => setFormData({ ...formData, hostel_id: e.target.value })}
                    >
                      {hostels.map((hostel) => (
                        <MenuItem key={hostel.hostel_id} value={hostel.hostel_id}>
                          {hostel.hostel_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Room Number"
                    value={formData.room_number}
                    onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Room Type</InputLabel>
                    <Select
                      value={formData.room_type}
                      label="Room Type"
                      onChange={(e) => setFormData({ ...formData, room_type: e.target.value })}
                    >
                      {roomTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Room Capacity"
                    type="number"
                    value={formData.room_capacity}
                    onChange={(e) => setFormData({ ...formData, room_capacity: e.target.value })}
                    inputProps={{ min: 1 }}
                    required
                  />
                </Grid>
              </>
            )}

            {dialogType === 'allocation' && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Student</InputLabel>
                    <Select
                      value={formData.student_id}
                      label="Student"
                      onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                    >
                      {students.map((student) => (
                        <MenuItem key={student.student_id} value={student.student_id}>
                          {student.first_name} {student.last_name} ({student.student_code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Room</InputLabel>
                    <Select
                      value={formData.room_id}
                      label="Room"
                      onChange={(e) => setFormData({ ...formData, room_id: e.target.value })}
                    >
                      {rooms.map((room) => (
                        <MenuItem key={room.room_id} value={room.room_id}>
                          {room.hostel_name} - Room {room.room_number} ({room.room_type}, {room.current_occupancy || 0}/{room.capacity})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Allocation Date"
                    type="date"
                    value={formData.allocation_date}
                    onChange={(e) => setFormData({ ...formData, allocation_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Fee Amount (Rs.)"
                    type="number"
                    value={formData.fee_amount}
                    onChange={(e) => setFormData({ ...formData, fee_amount: e.target.value })}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HostelManagement;
